import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { zustandPersistStorage } from '../appState/zustandPersistStorage';
import type { MovementId } from '../config/movements';
import { getMovement } from '../config/movements';
import type { RfidDocumentRow } from '../services/rfid.service';
import {
  emptyTransferDetails,
  type KitGroup,
  type TransferDetails,
  type TransportDetails,
  type WizardLine,
  type WizardVariant,
} from '../types/wizard';
import {
  buildLinesFromTransferItems,
} from '../utils/transferDocument';
import { detectWizardVariant, formatDateEnGb } from '../utils/document';
import { buildLinesFromDocumentItems } from '../utils/kitDocument';
import { rfidService } from '../services/rfid.service';
import {
  normalizeTagId,
  syncWizardFromTagSet,
  applySyncedLinesToKitGroups,
  tagsForLineInKit,
  type KitBomItem,
} from '../utils/tagAllocation';
import { isLeapTag } from '../utils/leapDetect';
import { engineService } from '../services/engine.service';
import { toast } from 'sonner';
import { clampManualNonScanLines, componentRequiredQty } from '../utils/lineQuantities';
import {
  resolveOptionValue,
  TRANSPORT_BY_LIST,
  TRANSPORT_MODES,
  VEHICLE_TYPES,
} from '../config/transportUdf';
import { sanitizeDriverMobile } from '../utils/transportFieldUtils';
import type { CustomerPostingContext } from '../utils/customerDetails';
import {
  formatCustomerDetailsLoadError,
  movementRequiresCustomerPosting,
} from '../utils/postingPreview';

const STORAGE_KEY = 'rfid-document-wizard-draft-v1';

const emptyCustomerPosting = {
  customerLob: '',
  customerLobText: '',
  customerAccountGroup: '',
  useAutoTransferType: false,
  customerPostingCustomerCode: '',
  customerDetailsError: null as string | null,
  customerDetailsLoaded: false,
};

const LEAP_LOOKUP_DEBOUNCE_MS = 250;
let leapLookupTimer: ReturnType<typeof setTimeout> | null = null;
const leapLookupQueue = new Set<string>();

function emptySkuCache() {
  return {
    skuByTag: {} as Record<string, string | null>,
    leapLookupPending: false,
  };
}

function applyTagSync(
  get: () => WizardState,
  set: (
    partial: Partial<WizardState> | ((s: WizardState) => Partial<WizardState>)
  ) => void
) {
  const s = get();
  let synced = syncWizardFromTagSet({
    tagSet: s.tagSet,
    lines: s.lines,
    kitGroups: s.kitGroups,
    bom: s.kitBom,
    skuByTag: s.skuByTag,
  });
  const lines = clampManualNonScanLines(synced.lines, synced.kitGroups, s.kitBom);
  synced = {
    lines,
    kitGroups: applySyncedLinesToKitGroups(lines, synced.kitGroups),
  };
  set(synced);
}

function scheduleLeapLookups(
  get: () => WizardState,
  set: (
    partial: Partial<WizardState> | ((s: WizardState) => Partial<WizardState>)
  ) => void
) {
  if (leapLookupQueue.size === 0) return;
  set({ leapLookupPending: true });
  if (leapLookupTimer) clearTimeout(leapLookupTimer);
  leapLookupTimer = setTimeout(() => {
    void flushLeapLookups(get, set);
  }, LEAP_LOOKUP_DEBOUNCE_MS);
}

async function flushLeapLookups(
  get: () => WizardState,
  set: (
    partial: Partial<WizardState> | ((s: WizardState) => Partial<WizardState>)
  ) => void
) {
  leapLookupTimer = null;
  const codes = [...leapLookupQueue];
  leapLookupQueue.clear();
  if (codes.length === 0) {
    set({ leapLookupPending: false });
    return;
  }

  try {
    const result = await engineService.lookupBatch(codes);
    const next: Record<string, string | null> = { ...get().skuByTag };
    for (const row of result.found ?? []) {
      const key = normalizeTagId(row.rfid_code);
      next[key] = row.sku_code?.trim() ? row.sku_code.trim() : null;
    }
    for (const missing of result.missing ?? []) {
      next[normalizeTagId(missing)] = null;
    }
    for (const code of codes) {
      const key = normalizeTagId(code);
      if (!Object.prototype.hasOwnProperty.call(next, key)) {
        next[key] = null;
      }
    }
    set({ skuByTag: next, leapLookupPending: leapLookupQueue.size > 0 });
    applyTagSync(get, set);
  } catch (err) {
    set({ leapLookupPending: leapLookupQueue.size > 0 });
    const message =
      err && typeof err === 'object' && 'message' in err
        ? String((err as { message?: unknown }).message ?? '')
        : '';
    toast.error(message || 'Could not resolve SKU for LEAP tags. Legacy tags still allocate.');
  }
}

function emptyTransport(doc: RfidDocumentRow): TransportDetails {
  return {
    TRANSPORTER_CODE: String(doc.TransporterCode ?? ''),
    TRANSPORTER_NAME: String(doc.TransporterName ?? doc.TRANSPORT_NAME ?? ''),
    VEHICLE_NO: String(doc.VehicleNo ?? ''),
    VEHICLE_TYPE: resolveOptionValue(VEHICLE_TYPES, String(doc.VehicleType ?? '')),
    DRIVER_NAME: String(doc.DriverName ?? ''),
    DRIVER_MOB_NO: sanitizeDriverMobile(doc.DriverMobileNo),
    TRANPORTATION_BY: resolveOptionValue(
      TRANSPORT_BY_LIST,
      String(doc.TransportationBy ?? doc.TransporterCode ?? '')
    ),
    INSPECTEDBY: String(doc.InspectedBy ?? ''),
    LR_NO: String(doc.LR ?? ''),
    DIRECT_DISPLATCH: 'N',
    distance: String(doc.Distance ?? ''),
    reason: String(doc.Reason ?? ''),
    containerSeal: String(doc.ContainerSeal ?? ''),
    eway: String(doc.EwayTranstype ?? ''),
    pod: String(doc.PodReceived ?? doc.POD ?? 'N'),
    TRANSPORT_MODE: resolveOptionValue(TRANSPORT_MODES, String(doc.ModeofTransport ?? '')),
    freight: String(doc.Freight ?? '').trim() || '0',
  };
}

function lineFromItem(
  item: Record<string, unknown>,
  movementId: MovementId,
  idx: number
): WizardLine {
  const m = getMovement(movementId);
  const qty = parseInt(String(item.Quantity ?? '0'), 10) || 0;
  const scannable = String(item.Scannable ?? '') === 'X';
  return {
    id: `${item.ItemNo ?? idx}-${item.Material ?? idx}`,
    itemNo: String(item.ItemNo ?? ''),
    materialCode: String(item.Material ?? item.Component ?? ''),
    materialDesc: String(
      item.MaterialDesc ?? item.Description ?? item.ComponentDesc ?? item.Component ?? ''
    ),
    quantity: qty,
    scannable,
    scanQty: 0,
    nonScanQty: null,
    shortQty: 0,
    fromBay: String(item.FromBay ?? item.fromBay ?? ''),
    fromBin: m.fromBin ?? String(item.FromBin ?? ''),
    toBay: m.toBay ?? String(item.ToBay ?? item.TO_BAY ?? ''),
    toBin: m.toBin ?? String(item.ToBin ?? ''),
    sender: String(item.Sender ?? item.Plant ?? ''),
    senderName: String(item.Sender_Name ?? item.SenderName ?? item.PlantName ?? ''),
    materialGroupCode: String(item.Material_GC ?? item.MaterialGC ?? ''),
    materialGroupName: String(item.Material_GCN ?? item.MaterialGCN ?? ''),
    rfidCodes: [],
  };
}


type WizardState = {
  movementId: MovementId | null;
  document: RfidDocumentRow | null;
  variant: WizardVariant;
  effectiveDate: Date;
  transport: TransportDetails;
  lines: WizardLine[];
  kitGroups: KitGroup[];
  activeKitIndex: number;
  remarks: string;
  selectedLineId: string | null;
  transferBpCode: string;
  transferCustomerName: string;
  transferToBpCode: string;
  transferToCustomerName: string;
  transferLineOfBusiness: string;
  transferDetails: TransferDetails;
  transferBaseDocNo: string;
  tagSet: Set<string>;
  skuByTag: Record<string, string | null>;
  leapLookupPending: boolean;
  kitBom: KitBomItem[] | null;
  /** Active document wizard — used for seamless scanner reconnect. */
  wizardActive: boolean;
  resumeScanAfterConnect: boolean;
  /** Last successful SAP submit — kept for success / View Doc until reset. */
  submitMessage: string;
  submitRaw: unknown;
  /** From SAP customer_details — drives AUT vs manual TRANSFER_TYPE on submit. */
  customerLob: string;
  customerLobText: string;
  customerAccountGroup: string;
  useAutoTransferType: boolean;
  customerPostingCustomerCode: string;
  customerDetailsError: string | null;
  customerDetailsLoaded: boolean;

  initFromDocument: (movementId: MovementId, document: RfidDocumentRow) => void;
  initTransferOut: (
    movementId: MovementId,
    fromBpCode: string,
    fromCustomerName: string,
    toBpCode: string,
    toCustomerName: string,
    items: Record<string, unknown>[],
    lineOfBusiness?: string
  ) => void;
  initTransferIn: (
    movementId: MovementId,
    fromBpCode: string,
    fromCustomerName: string,
    baseDocument: RfidDocumentRow
  ) => void;
  setTransferDetails: (patch: Partial<TransferDetails>) => void;
  setTransport: (patch: Partial<TransportDetails>) => void;
  setEffectiveDate: (d: Date) => void;
  setRemarks: (r: string) => void;
  setActiveKitIndex: (i: number) => void;
  setSelectedLineId: (id: string | null) => void;
  setLineNonScanQty: (lineId: string, value: string, kitIndex?: number) => void;
  setLineFromBay: (lineId: string, fromBay: string) => void;
  setLineFromBin: (lineId: string, fromBin: string) => void;
  setLineToBay: (lineId: string, toBay: string) => void;
  setLineShortQty: (lineId: string, value: string, kitIndex?: number) => void;
  addTagToLine: (lineId: string, tag: string) => void;
  removeTagFromLine: (lineId: string, tag: string) => void;
  ingestTag: (tagId: string) => void;
  removeTagFromSet: (tagId: string) => void;
  getTagsForLine: (line: WizardLine, kitIndex: number) => string[];
  getEffectiveDateFormatted: () => string;
  setResumeScanAfterConnect: (v: boolean) => void;
  setSubmitResult: (message: string, raw: unknown) => void;
  setCustomerPostingContext: (ctx: CustomerPostingContext) => void;
  loadCustomerPostingContext: (customerCode: string) => Promise<void>;
  reset: () => void;
};

const initial = {
  movementId: null as MovementId | null,
  document: null as RfidDocumentRow | null,
  variant: 'flat' as WizardVariant,
  effectiveDate: new Date(),
  transport: emptyTransport({}),
  lines: [] as WizardLine[],
  kitGroups: [] as KitGroup[],
  activeKitIndex: 0,
  remarks: '',
  selectedLineId: null as string | null,
  transferBpCode: '',
  transferCustomerName: '',
  transferToBpCode: '',
  transferToCustomerName: '',
  transferLineOfBusiness: '',
  transferDetails: emptyTransferDetails(),
  transferBaseDocNo: '',
  tagSet: new Set<string>(),
  skuByTag: {} as Record<string, string | null>,
  leapLookupPending: false,
  kitBom: null as KitBomItem[] | null,
  wizardActive: false,
  resumeScanAfterConnect: false,
  submitMessage: '',
  submitRaw: null as unknown,
  ...emptyCustomerPosting,
};

type PersistedWizardDraft = {
  movementId: MovementId | null;
  document: RfidDocumentRow | null;
  variant: WizardVariant;
  effectiveDate: string;
  transport: TransportDetails;
  lines: WizardLine[];
  kitGroups: KitGroup[];
  activeKitIndex: number;
  remarks: string;
  selectedLineId: string | null;
  transferBpCode: string;
  transferCustomerName: string;
  transferToBpCode: string;
  transferToCustomerName: string;
  transferLineOfBusiness: string;
  transferDetails: TransferDetails;
  transferBaseDocNo: string;
  tagSet: string[];
  skuByTag?: Record<string, string | null>;
  kitBom: KitBomItem[] | null;
  wizardActive: boolean;
  submitMessage: string;
  submitRaw: unknown;
  customerLob: string;
  customerLobText: string;
  customerAccountGroup: string;
  useAutoTransferType: boolean;
  customerPostingCustomerCode: string;
  customerDetailsError: string | null;
  customerDetailsLoaded: boolean;
};

function revivePersistedDraft(persisted: PersistedWizardDraft) {
  const tagSet = new Set(
    Array.isArray(persisted.tagSet) ? persisted.tagSet.map(normalizeTagId).filter(Boolean) : []
  );
  const effectiveDate = persisted.effectiveDate
    ? new Date(persisted.effectiveDate)
    : new Date();
  const dateOk = !Number.isNaN(effectiveDate.getTime());

  let lines = Array.isArray(persisted.lines) ? persisted.lines : [];
  let kitGroups = Array.isArray(persisted.kitGroups) ? persisted.kitGroups : [];
  const kitBom = persisted.kitBom ?? null;

  const skuByTag = { ...(persisted.skuByTag ?? {}) };
  if (tagSet.size > 0 && lines.length > 0) {
    const synced = syncWizardFromTagSet({ tagSet, lines, kitGroups, bom: kitBom, skuByTag });
    const clamped = clampManualNonScanLines(synced.lines, synced.kitGroups, kitBom);
    lines = clamped;
    kitGroups = applySyncedLinesToKitGroups(clamped, synced.kitGroups);
  }

  return {
    movementId: persisted.movementId,
    document: persisted.document,
    variant: persisted.variant ?? 'flat',
    effectiveDate: dateOk ? effectiveDate : new Date(),
    transport: { ...emptyTransport({}), ...(persisted.transport ?? {}) },
    lines,
    kitGroups,
    activeKitIndex: persisted.activeKitIndex ?? 0,
    remarks: persisted.remarks ?? '',
    selectedLineId: persisted.selectedLineId ?? null,
    transferBpCode: persisted.transferBpCode ?? '',
    transferCustomerName: persisted.transferCustomerName ?? '',
    transferToBpCode: persisted.transferToBpCode ?? '',
    transferToCustomerName: persisted.transferToCustomerName ?? '',
    transferLineOfBusiness: persisted.transferLineOfBusiness ?? '',
    transferDetails: persisted.transferDetails ?? emptyTransferDetails(),
    transferBaseDocNo: persisted.transferBaseDocNo ?? '',
    tagSet,
    skuByTag,
    leapLookupPending: false,
    kitBom,
    wizardActive: Boolean(persisted.document && persisted.movementId),
    resumeScanAfterConnect: false,
    submitMessage: persisted.submitMessage ?? '',
    submitRaw: persisted.submitRaw ?? null,
    customerLob: persisted.customerLob ?? '',
    customerLobText: persisted.customerLobText ?? '',
    customerAccountGroup: persisted.customerAccountGroup ?? '',
    useAutoTransferType: Boolean(persisted.useAutoTransferType),
    customerPostingCustomerCode: persisted.customerPostingCustomerCode ?? '',
    customerDetailsError: persisted.customerDetailsError ?? null,
    customerDetailsLoaded: Boolean(persisted.customerDetailsLoaded),
  };
}

export const useDocumentWizardStore = create<WizardState>()(
  persist(
    (set, get) => ({
  ...initial,

  initFromDocument: (movementId, document) => {
    const variant = detectWizardVariant(document);
    const { lines, kitGroups } = buildLinesFromDocumentItems(document, movementId, variant);
    set({
      movementId,
      document,
      variant,
      effectiveDate: new Date(),
      transport: emptyTransport(document),
      lines,
      kitGroups,
      activeKitIndex: 0,
      remarks: '',
      selectedLineId: lines.find((l) => l.scannable && !l.isKitHeader)?.id ?? null,
      transferBpCode: '',
      transferCustomerName: '',
      transferToBpCode: '',
      transferToCustomerName: '',
      transferLineOfBusiness: '',
      transferDetails: emptyTransferDetails(),
      transferBaseDocNo: '',
      tagSet: new Set(),
      ...emptySkuCache(),
      kitBom: null,
      wizardActive: true,
      resumeScanAfterConnect: false,
      submitMessage: '',
      submitRaw: null,
      ...emptyCustomerPosting,
    });
    if (variant === 'kit' && document.DocumentNo) {
      void rfidService.fetchKitBom(String(document.DocumentNo)).then((bom) => set({ kitBom: bom }));
    }
  },

  initTransferOut: (movementId, fromBpCode, fromCustomerName, toBpCode, toCustomerName, items, lineOfBusiness) => {
    const { lines, kitGroups, variant } = buildLinesFromTransferItems(items, movementId);
    const pseudoDoc: RfidDocumentRow = {
      DocumentNo: 'TROUT',
      CustName: fromCustomerName,
      Receiver: fromBpCode,
      Customer: toCustomerName,
    };
    set({
      movementId,
      document: pseudoDoc,
      variant,
      effectiveDate: new Date(),
      transport: emptyTransport(pseudoDoc),
      lines,
      kitGroups,
      activeKitIndex: 0,
      remarks: '',
      selectedLineId: lines.find((l) => !l.isKitHeader)?.id ?? null,
      transferBpCode: fromBpCode,
      transferCustomerName: fromCustomerName,
      transferToBpCode: toBpCode,
      transferToCustomerName: toCustomerName,
      transferLineOfBusiness: lineOfBusiness ?? '',
      transferDetails: emptyTransferDetails(),
      transferBaseDocNo: '',
      tagSet: new Set(),
      ...emptySkuCache(),
      kitBom: null,
      wizardActive: true,
      resumeScanAfterConnect: false,
      submitMessage: '',
      submitRaw: null,
      ...emptyCustomerPosting,
    });
    if (variant === 'kit') {
      void rfidService.fetchKitBom('TRANSFER').then((bom) => set({ kitBom: bom }));
    }
  },

  initTransferIn: (movementId, fromBpCode, fromCustomerName, baseDocument) => {
    const doc = baseDocument;
    const variant = detectWizardVariant(doc);
    const { lines, kitGroups } = buildLinesFromDocumentItems(doc, movementId, variant);
    set({
      movementId,
      document: doc,
      variant,
      effectiveDate: new Date(),
      transport: emptyTransport(doc),
      lines,
      kitGroups,
      activeKitIndex: 0,
      remarks: '',
      selectedLineId: lines.find((l) => !l.isKitHeader)?.id ?? null,
      transferBpCode: fromBpCode,
      transferCustomerName: fromCustomerName,
      transferToBpCode: String(doc.Receiver ?? doc.Sender ?? ''),
      transferToCustomerName: fromCustomerName,
      transferLineOfBusiness: '',
      transferDetails: emptyTransferDetails(),
      transferBaseDocNo: String(doc.DocumentNo ?? ''),
      tagSet: new Set(),
      ...emptySkuCache(),
      kitBom: null,
      wizardActive: true,
      resumeScanAfterConnect: false,
      submitMessage: '',
      submitRaw: null,
      ...emptyCustomerPosting,
    });
    if (variant === 'kit' && doc.DocumentNo) {
      void rfidService.fetchKitBom(String(doc.DocumentNo)).then((bom) => set({ kitBom: bom }));
    }
  },

  setTransferDetails: (patch) =>
    set((s) => ({ transferDetails: { ...s.transferDetails, ...patch } })),

  setResumeScanAfterConnect: (resumeScanAfterConnect) => set({ resumeScanAfterConnect }),

  setTransport: (patch) => set((s) => ({ transport: { ...s.transport, ...patch } })),
  setEffectiveDate: (effectiveDate) => set({ effectiveDate }),
  setRemarks: (remarks) => set({ remarks }),
  setActiveKitIndex: (activeKitIndex) => set({ activeKitIndex }),
  setSelectedLineId: (selectedLineId) => set({ selectedLineId }),

  setLineFromBay: (lineId, fromBay) => {
    set((s) => {
      const bay = fromBay.trim();
      const lines = s.lines.map((l) => (l.id === lineId ? { ...l, fromBay: bay } : l));
      const kitGroups = applySyncedLinesToKitGroups(lines, s.kitGroups);
      return { lines, kitGroups };
    });
  },

  setLineFromBin: (lineId, fromBin) => {
    set((s) => {
      const bin = fromBin.trim();
      const lines = s.lines.map((l) => (l.id === lineId ? { ...l, fromBin: bin } : l));
      const kitGroups = applySyncedLinesToKitGroups(lines, s.kitGroups);
      return { lines, kitGroups };
    });
  },

  setLineToBay: (lineId, toBay) => {
    set((s) => {
      const bay = toBay.trim();
      const lines = s.lines.map((l) => (l.id === lineId ? { ...l, toBay: bay } : l));
      const kitGroups = applySyncedLinesToKitGroups(lines, s.kitGroups);
      return { lines, kitGroups };
    });
  },

  setLineShortQty: (lineId, value, kitIndex) => {
    set((s) => {
      const line = s.lines.find((l) => l.id === lineId);
      if (!line) return s;
      const ki =
        kitIndex ??
        (s.kitGroups.length > 0
          ? s.kitGroups.findIndex((g) => g.lines.some((l) => l.id === lineId))
          : 0);
      const kitIdx = ki >= 0 ? ki : 0;
      const required = componentRequiredQty(line, s.kitGroups, kitIdx, s.kitBom);

      let shortQty = 0;
      if (value.trim() !== '') {
        const n = parseInt(value.replace(/\D/g, ''), 10);
        shortQty = Number.isNaN(n) ? 0 : Math.max(0, Math.min(n, required));
      }

      const lines = s.lines.map((l) => (l.id === lineId ? { ...l, shortQty } : l));
      const kitGroups = applySyncedLinesToKitGroups(lines, s.kitGroups);
      return { lines, kitGroups };
    });
  },

  setLineNonScanQty: (lineId, value, kitIndex) => {
    set((s) => {
      const line = s.lines.find((l) => l.id === lineId);
      if (!line) return s;
      const ki =
        kitIndex ??
        (s.kitGroups.length > 0
          ? s.kitGroups.findIndex((g) => g.lines.some((l) => l.id === lineId))
          : 0);
      const kitIdx = ki >= 0 ? ki : 0;
      const required = componentRequiredQty(line, s.kitGroups, kitIdx, s.kitBom);

      let nonScanQty: number | null = null;
      if (value.trim() !== '') {
        const n = parseInt(value.replace(/\D/g, ''), 10);
        nonScanQty = Number.isNaN(n) ? 0 : Math.max(0, n);
        const maxNonScan = Math.max(0, required - line.scanQty);
        nonScanQty = Math.min(nonScanQty, maxNonScan);
      }

      const lines = s.lines.map((l) =>
        l.id === lineId ? { ...l, nonScanQty } : l
      );
      const kitGroups = applySyncedLinesToKitGroups(lines, s.kitGroups);
      return { lines, kitGroups };
    });
  },

  addTagToLine: (lineId, tag) => {
    const t = tag.trim();
    if (!t) return;
    set((s) => ({
      lines: s.lines.map((line) => {
        if (line.id !== lineId) return line;
        if (line.rfidCodes.includes(t)) return line;
        const rfidCodes = [...line.rfidCodes, t];
        return { ...line, rfidCodes, scanQty: rfidCodes.length };
      }),
    }));
  },

  removeTagFromLine: (lineId, tag) => {
    get().removeTagFromSet(tag);
  },

  ingestTag: (tagId) => {
    const t = normalizeTagId(tagId);
    if (!t) return;

    set((s) => {
      const tagSet = new Set(s.tagSet);
      if (!tagSet.has(t)) tagSet.add(t);
      let synced = syncWizardFromTagSet({
        tagSet,
        lines: s.lines,
        kitGroups: s.kitGroups,
        bom: s.kitBom,
        skuByTag: s.skuByTag,
      });
      const lines = clampManualNonScanLines(synced.lines, synced.kitGroups, s.kitBom);
      synced = {
        lines,
        kitGroups: applySyncedLinesToKitGroups(lines, synced.kitGroups),
      };
      return { tagSet, ...synced };
    });

    if (isLeapTag(t)) {
      const cached = get().skuByTag;
      if (!Object.prototype.hasOwnProperty.call(cached, t)) {
        leapLookupQueue.add(t);
        scheduleLeapLookups(get, set);
      }
    }
  },

  removeTagFromSet: (tagId) => {
    set((s) => {
      const key = normalizeTagId(tagId);
      const tagSet = new Set(s.tagSet);
      tagSet.delete(key);
      const synced = syncWizardFromTagSet({
        tagSet,
        lines: s.lines,
        kitGroups: s.kitGroups,
        bom: s.kitBom,
        skuByTag: s.skuByTag,
      });
      return { tagSet, ...synced };
    });
  },

  getTagsForLine: (line, kitIndex) => {
    const s = get();
    return tagsForLineInKit(s.tagSet, line, s.kitGroups, kitIndex, s.kitBom, s.lines, s.skuByTag);
  },

  getEffectiveDateFormatted: () => formatDateEnGb(get().effectiveDate),

  setSubmitResult: (message, raw) => set({ submitMessage: message, submitRaw: raw }),

  setCustomerPostingContext: (ctx) =>
    set({
      customerLob: ctx.lob,
      customerLobText: ctx.lobText,
      customerAccountGroup: ctx.accountGroup,
      useAutoTransferType: ctx.useAutoTransferType,
      transferLineOfBusiness: ctx.lobCode || '',
    }),

  loadCustomerPostingContext: async (customerCode) => {
    const movementId = get().movementId;
    const movement = movementId ? getMovement(movementId) : null;
    const requiresCustomerDetails = movement ? movementRequiresCustomerPosting(movement) : true;
    const code = String(customerCode ?? '').trim();

    if (!requiresCustomerDetails) {
      set({
        ...emptyCustomerPosting,
        customerPostingCustomerCode: code,
        customerDetailsError: null,
        customerDetailsLoaded: true,
      });
      return;
    }

    if (!code) {
      set({
        ...emptyCustomerPosting,
        customerPostingCustomerCode: '',
        customerDetailsError: 'Customer number is missing on this document.',
        customerDetailsLoaded: true,
      });
      return;
    }

    set({
      customerPostingCustomerCode: code,
      customerDetailsError: null,
      customerDetailsLoaded: false,
    });

    try {
      const ctx = await rfidService.fetchCustomerDetails(code);
      get().setCustomerPostingContext(ctx);
      set({
        customerDetailsError: null,
        customerDetailsLoaded: true,
      });
    } catch (error) {
      set({
        ...emptyCustomerPosting,
        customerPostingCustomerCode: code,
        customerDetailsError: formatCustomerDetailsLoadError(error),
        customerDetailsLoaded: true,
      });
    }
  },

  reset: () => {
    if (leapLookupTimer) {
      clearTimeout(leapLookupTimer);
      leapLookupTimer = null;
    }
    leapLookupQueue.clear();
    set({
      ...initial,
      effectiveDate: new Date(),
      transport: emptyTransport({}),
      transferDetails: emptyTransferDetails(),
      tagSet: new Set(),
      ...emptySkuCache(),
      kitBom: null,
      submitMessage: '',
      submitRaw: null,
      ...emptyCustomerPosting,
    });
  },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => zustandPersistStorage),
      partialize: (s): PersistedWizardDraft => ({
        movementId: s.movementId,
        document: s.document,
        variant: s.variant,
        effectiveDate:
          s.effectiveDate instanceof Date
            ? s.effectiveDate.toISOString()
            : new Date(s.effectiveDate as unknown as string).toISOString(),
        transport: s.transport,
        lines: s.lines,
        kitGroups: s.kitGroups,
        activeKitIndex: s.activeKitIndex,
        remarks: s.remarks,
        selectedLineId: s.selectedLineId,
        transferBpCode: s.transferBpCode,
        transferCustomerName: s.transferCustomerName,
        transferToBpCode: s.transferToBpCode,
        transferToCustomerName: s.transferToCustomerName,
        transferLineOfBusiness: s.transferLineOfBusiness,
        transferDetails: s.transferDetails,
        transferBaseDocNo: s.transferBaseDocNo,
        tagSet: Array.from(s.tagSet),
        skuByTag: s.skuByTag,
        kitBom: s.kitBom,
        wizardActive: s.wizardActive,
        submitMessage: s.submitMessage,
        submitRaw: s.submitRaw,
        customerLob: s.customerLob,
        customerLobText: s.customerLobText,
        customerAccountGroup: s.customerAccountGroup,
        useAutoTransferType: s.useAutoTransferType,
        customerPostingCustomerCode: s.customerPostingCustomerCode,
        customerDetailsError: s.customerDetailsError,
        customerDetailsLoaded: s.customerDetailsLoaded,
      }),
      merge: (persisted, current) => {
        if (!persisted || typeof persisted !== 'object') return current;
        const draft = persisted as PersistedWizardDraft;
        if (!draft.document || !draft.movementId) return current;
        return {
          ...current,
          ...revivePersistedDraft(draft),
        };
      },
      onRehydrateStorage: () => () => {
        queueMicrotask(() => {
          const s = useDocumentWizardStore.getState();
          for (const raw of s.tagSet) {
            const t = normalizeTagId(raw);
            if (isLeapTag(t) && !Object.prototype.hasOwnProperty.call(s.skuByTag, t)) {
              leapLookupQueue.add(t);
            }
          }
          if (leapLookupQueue.size > 0) {
            scheduleLeapLookups(useDocumentWizardStore.getState, useDocumentWizardStore.setState);
          }

          if (s.variant !== 'kit' || !s.document) return;
          if (s.kitBom && s.kitBom.length > 0) return;
          const docNo =
            s.movementId === 'transfer_out'
              ? 'TRANSFER'
              : String(s.document.DocumentNo ?? '');
          if (!docNo) return;
          void rfidService.fetchKitBom(docNo).then((bom) => {
            const cur = useDocumentWizardStore.getState();
            if (cur.variant !== 'kit' || !cur.document) return;
            const synced = syncWizardFromTagSet({
              tagSet: cur.tagSet,
              lines: cur.lines,
              kitGroups: cur.kitGroups,
              bom,
              skuByTag: cur.skuByTag,
            });
            const lines = clampManualNonScanLines(synced.lines, synced.kitGroups, bom);
            useDocumentWizardStore.setState({
              kitBom: bom,
              lines,
              kitGroups: applySyncedLinesToKitGroups(lines, synced.kitGroups),
            });
          });
        });
      },
    }
  )
);
